import { Box, Button, Flex, Select, Spinner, Text } from '@chakra-ui/react';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import {
  articleCertificateStyles as styles,
  createArticleStyles,
} from '@texas/resources/theme/styles/entityWorkspace';
import { useTranslation } from 'react-i18next';
import { ArticleCertificatesTable } from './certificates/ArticleCertificatesTable';
import { useArticleCertificates } from './certificates/useArticleCertificates';
import { ManagementCard } from './ManagementCard';
import { PendingChangesAction } from './PendingChangesAction';

interface Props {
  articleId: number;
  customerId: number;
}

export function CertificatesManagement({ articleId, customerId }: Props) {
  const { t } = useTranslation();
  const certificates = useArticleCertificates(articleId, customerId);
  const { resources, loading, error, mutation, form } = certificates;
  const branches = resources.branches.data;

  return (
    <Box as="form" onSubmit={certificates.save} sx={styles.page}>
      <ManagementCard
        className="article-certificate-card"
        title={t('article.create.certificateLabel')}
        description={t('article.management.certificatesDescription')}
        action={
          branches && (
            <Text sx={createArticleStyles.muted}>
              {t('article.management.certificateBranchCount', {
                count: branches.length,
              })}
            </Text>
          )
        }
      >
        {(loading || error || mutation.error) && (
          <Box sx={styles.messages}>
            {loading && <Spinner />}
            {error && <ErrorDetails error={error} />}
            {mutation.error && <ErrorDetails error={mutation.error} />}
          </Box>
        )}
        {!loading && !error && branches && (
          <Box>
            <Flex sx={styles.filters}>
              <Box sx={styles.regionFilter}>
                <Select
                  aria-label={t('article.management.allRegions')}
                  value={certificates.regionId}
                  onChange={(event) =>
                    certificates.setRegionId(event.target.value)
                  }
                >
                  <option value="">{t('article.management.allRegions')}</option>
                  {certificates.availableRegions.map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Text sx={createArticleStyles.branchCount}>
                {t('article.management.showingBranches', {
                  visible: certificates.visibleBranches.length,
                  total: branches.length,
                })}
              </Text>
            </Flex>
            <Box sx={createArticleStyles.tableScroll}>
              <ArticleCertificatesTable
                branches={certificates.visibleBranches}
                allBranches={branches}
                definitions={resources.definitions.data}
                customerRequirements={resources.customerRequirements.data ?? []}
                regions={resources.regions.data}
                control={form.control}
                isDisabled={mutation.loading}
              />
            </Box>
            <Flex sx={styles.footer}>
              <Box>
                <Text sx={createArticleStyles.muted}>
                  {certificates.hasPendingChanges
                    ? t('article.management.unsavedCertificateBranches', {
                        count: certificates.changedBranchCount,
                      })
                    : t('article.management.noUnsavedChanges')}
                </Text>
                <Text sx={createArticleStyles.muted}>
                  {t('article.management.manageConnectedBranches')}
                </Text>
              </Box>
              <Flex sx={createArticleStyles.row}>
                {certificates.hasPendingChanges && (
                  <Button
                    variant="outline"
                    onClick={() => certificates.discard()}
                    isDisabled={mutation.loading}
                  >
                    {t('article.management.discardCertificateChanges')}
                  </Button>
                )}
                <PendingChangesAction
                  hasPendingChanges={certificates.hasPendingChanges}
                  isSaving={mutation.loading}
                />
              </Flex>
            </Flex>
          </Box>
        )}
      </ManagementCard>
    </Box>
  );
}
